import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  CircularProgress,
  Tooltip
} from '@mui/material'
import {
  Add,
  Edit,
  Delete,
  Business,
  Visibility,
  SmartToy
} from '@mui/icons-material'
import axios from 'axios'
import { useAuth } from '../../contexts/AuthContext'

const ProcesosList = () => {
  const { user } = useAuth()
  const [procesos, setProcesos] = useState([])
  const [gestiones, setGestiones] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [openDialog, setOpenDialog] = useState(false)
  const [editingProceso, setEditingProceso] = useState(null)
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    gestion_id: '',
    activo: true
  })
  const [aiLoading, setAiLoading] = useState(false)
  const [statusMessage, setStatusMessage] = useState('')
  const [showStatusBar, setShowStatusBar] = useState(false)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token')
      
      const [procesosResp, gestionesResp] = await Promise.all([
        axios.get('/api/procesos', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get('/api/gestiones', {
          headers: { Authorization: `Bearer ${token}` }
        })
      ])
      
      setProcesos(procesosResp.data || [])
      setGestiones(gestionesResp.data || [])
    } catch (error) {
      setError('Error al cargar datos: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const canCreate = () => {
    return user?.rol === 'administrador' || user?.rol === 'editor'
  }

  const canEdit = (proceso) => {
    if (user?.rol === 'administrador') return true
    if (user?.rol === 'editor' && proceso.created_by === user.id) return true
    return false
  }

  const canDelete = (proceso) => {
    return user?.rol === 'administrador'
  }

  const openCreateDialog = () => {
    const defaultGestionId = user?.rol === 'editor' ? user.gestion_id : ''
    
    setFormData({
      nombre: '',
      descripcion: '',
      gestion_id: defaultGestionId,
      activo: true
    })
    setEditingProceso(null)
    setOpenDialog(true)
  }

  const openEditDialog = (proceso) => {
    setFormData({
      nombre: proceso.nombre,
      descripcion: proceso.descripcion || '',
      gestion_id: proceso.gestion_id || '',
      activo: proceso.activo
    })
    setEditingProceso(proceso)
    setOpenDialog(true)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setEditingProceso(null)
    setError(null)
    setAiLoading(false)
    setShowStatusBar(false)
    setStatusMessage('')
  }

  // Función para mostrar mensajes de estado verbose
  const updateStatusMessage = (message) => {
    setStatusMessage(message)
    setShowStatusBar(true)
    console.log('📊 Status IA:', message)
  }

  const hideStatusBar = () => {
    setShowStatusBar(false)
    setStatusMessage('')
  }

  const generateDescriptionWithAI = async () => {
    if (!formData.nombre.trim()) {
      setError('Ingrese el nombre del proceso antes de generar la descripción con IA')
      return
    }

    setAiLoading(true)
    setError(null)
    updateStatusMessage('🤖 Iniciando generación de descripción con IA...')

    try {
      const token = localStorage.getItem('sgc_token')

      updateStatusMessage('📝 Construyendo prompt contextualizado para procesos educativos...')

      // Crear un prompt específico para procesos educativos
      const prompt = `Genera una descripción detallada y profesional para un proceso de gestión educativa en una institución de educación superior colombiana.

NOMBRE DEL PROCESO: ${formData.nombre}

CONTEXTO:
- Institución: Escuela Normal Superior del Alto Sinú (ENSAS)
- Sector: Educación Superior Pública en Colombia
- Marco: Sistema de Gestión de Calidad ISO 9001:2015
- Normatividad: MEN, CNA, CONACES, CESU

INSTRUCCIONES:
1. Genera una descripción de 150-250 palabras
2. Incluye el propósito del proceso
3. Menciona las actividades principales
4. Considera el contexto educativo colombiano
5. Usa lenguaje formal y técnico apropiado
6. Enfócate en la calidad educativa y mejora continua

Genera únicamente la descripción del proceso:`

      updateStatusMessage('🌐 Enviando solicitud al servicio de IA DeepSeek...')

      const response = await axios.post('/api/definiciones/generate-with-ai', {
        termino: `Proceso: ${formData.nombre}`,
        prompt: prompt
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000
      })

      updateStatusMessage('📦 Procesando respuesta de la IA...')

      if (response.data && response.data.definicion) {
        updateStatusMessage(`✅ Descripción generada: ${response.data.definicion.length} caracteres`)

        setFormData(prev => ({
          ...prev,
          descripcion: response.data.definicion
        }))

        setSuccess('Descripción generada con IA. Revise y ajuste según sea necesario.')
        updateStatusMessage('✅ Descripción aplicada al formulario exitosamente')

        // Ocultar la barra de estado después de 3 segundos
        setTimeout(() => hideStatusBar(), 3000)
      } else {
        updateStatusMessage('❌ Respuesta sin contenido válido')
        setError('No se pudo generar la descripción con IA')
        setTimeout(() => hideStatusBar(), 5000)
      }
    } catch (error) {
      console.error('Error generando descripción con IA:', error)

      let errorMessage = 'Error al generar descripción'
      if (error.response?.data?.detail) {
        errorMessage += ': ' + error.response.data.detail
      } else if (error.message) {
        errorMessage += ': ' + error.message
      }

      updateStatusMessage(`❌ ${errorMessage}`)
      setError(errorMessage)
      setTimeout(() => hideStatusBar(), 5000)
    } finally {
      setAiLoading(false)
    }
  }

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem('token')
      
      if (editingProceso) {
        // Editar proceso existente
        await axios.put(`/api/procesos/${editingProceso.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        })
        setSuccess('Proceso actualizado exitosamente')
      } else {
        // Crear nuevo proceso
        await axios.post('/api/procesos', formData, {
          headers: { Authorization: `Bearer ${token}` }
        })
        setSuccess('Proceso creado exitosamente')
      }
      
      handleCloseDialog()
      loadData()
    } catch (error) {
      setError('Error al guardar: ' + (error.response?.data?.detail || error.message))
    }
  }

  const handleDelete = async (proceso) => {
    if (!window.confirm(`¿Está seguro de eliminar el proceso "${proceso.nombre}"?`)) {
      return
    }

    try {
      const token = localStorage.getItem('token')
      await axios.delete(`/api/procesos/${proceso.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      
      setSuccess('Proceso eliminado exitosamente')
      loadData()
    } catch (error) {
      setError('Error al eliminar: ' + (error.response?.data?.detail || error.message))
    }
  }

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Cargando procesos...</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Business sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Gestión de Procesos
          </Typography>
        </Box>
        {canCreate() && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={openCreateDialog}
          >
            Nuevo Proceso
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell>Gestión</TableCell>
              {user?.rol === 'administrador' && <TableCell>Creado por</TableCell>}
              <TableCell>Estado</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {procesos.map((proceso) => (
              <TableRow key={proceso.id}>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {proceso.nombre}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {proceso.descripcion || 'Sin descripción'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {proceso.gestion_nombre ? (
                    <Chip
                      label={`${proceso.gestion_codigo} - ${proceso.gestion_nombre}`}
                      size="small"
                      sx={{
                        backgroundColor: `${proceso.gestion_color || '#64B5F6'}20`,
                        color: proceso.gestion_color || '#64B5F6',
                        border: `1px solid ${proceso.gestion_color || '#64B5F6'}`,
                        fontWeight: 'bold'
                      }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Sin gestión asignada
                    </Typography>
                  )}
                </TableCell>
                {user?.rol === 'administrador' && (
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {proceso.created_by_nombre || 'Sistema'}
                    </Typography>
                  </TableCell>
                )}
                <TableCell>
                  <Chip
                    label={proceso.activo ? 'Activo' : 'Inactivo'}
                    color={proceso.activo ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  {canEdit(proceso) && (
                    <IconButton
                      size="small"
                      onClick={() => openEditDialog(proceso)}
                      title="Editar"
                    >
                      <Edit />
                    </IconButton>
                  )}
                  {canDelete(proceso) && (
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(proceso)}
                      title="Eliminar"
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  )}
                  {!canEdit(proceso) && !canDelete(proceso) && (
                    <IconButton
                      size="small"
                      title="Solo lectura"
                      disabled
                    >
                      <Visibility />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {procesos.length === 0 && (
              <TableRow>
                <TableCell colSpan={user?.rol === 'administrador' ? 6 : 5} align="center">
                  <Typography color="text.secondary">
                    No hay procesos configurados
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog para crear/editar proceso */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingProceso ? 'Editar Proceso' : 'Nuevo Proceso'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label="Nombre del Proceso"
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              required
            />
            
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Typography variant="subtitle2" component="label">
                  Descripción
                </Typography>
                {!editingProceso && (
                  <Tooltip
                    title="Esta descripción debe revisarse por un humano ya que puede tener errores o ser imprecisa"
                    placement="top"
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={aiLoading ? <CircularProgress size={16} /> : <SmartToy />}
                      onClick={generateDescriptionWithAI}
                      disabled={aiLoading || !formData.nombre.trim()}
                      color="secondary"
                    >
                      {aiLoading ? 'Generando...' : 'Generar con IA (Verbose)'}
                    </Button>
                  </Tooltip>
                )}
              </Box>

              {/* Barra de estado verbose */}
              {showStatusBar && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 1,
                  p: 1,
                  bgcolor: 'rgba(25, 118, 210, 0.04)',
                  borderRadius: 1,
                  border: '1px solid rgba(25, 118, 210, 0.12)'
                }}>
                  <CircularProgress
                    size={16}
                    sx={{
                      mr: 1,
                      color: 'primary.main'
                    }}
                  />
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'primary.main' }}>
                    {statusMessage}
                  </Typography>
                </Box>
              )}
              <TextField
                fullWidth
                multiline
                rows={4}
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                placeholder="Descripción opcional del proceso"
                helperText={formData.descripcion && !editingProceso ? "⚠️ Descripción generada con IA. Revise y ajuste según sea necesario." : ""}
              />
            </Box>
            
            <FormControl fullWidth>
              <InputLabel>Gestión</InputLabel>
              <Select
                value={formData.gestion_id}
                label="Gestión"
                onChange={(e) => setFormData({ ...formData, gestion_id: e.target.value })}
                disabled={user?.rol === 'editor'} // Editores no pueden cambiar la gestión
              >
                {user?.rol === 'administrador' && (
                  <MenuItem value="">Sin gestión específica</MenuItem>
                )}
                {gestiones
                  .filter(g => g.activa)
                  .filter(g => user?.rol === 'administrador' || g.id === user?.gestion_id)
                  .map((gestion) => (
                    <MenuItem key={gestion.id} value={gestion.id}>
                      {gestion.codigo} - {gestion.nombre}
                    </MenuItem>
                  ))
                }
              </Select>
            </FormControl>
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.activo}
                  onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                />
              }
              label="Proceso activo"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.nombre.trim()}
          >
            {editingProceso ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ProcesosList